import React, { useCallback, useMemo } from 'react';
import { IconButton, Tooltip, Typography, makeStyles, MenuItem, Select } from '@material-ui/core';

import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import SkipNextOutlinedIcon from '@material-ui/icons/SkipNextOutlined';
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious';
import SkipPreviousOutlinedIcon from '@material-ui/icons/SkipPreviousOutlined';

import FastForwardIcon from '@material-ui/icons/FastForward';
import FastRewindIcon from '@material-ui/icons/FastRewind';
import { useHotkeys } from 'react-hotkeys-hook';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    height: '100%',
  },
  containerItem: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    height: '100%',
  },
  containerItemChild: {},
  inlineText: {
    display: 'inline-block',
    width: '45px',
    textAlign: 'center',
    fontSize: '14px',
  },
  mediaControlIcon: {
    color: '#444',
    width: '32px',
    height: '32px',
  },
  mediaControlSmallIcon: {
    width: '24px',
    height: '24px',
    color: '#777',
  },
  mediaControlBigIcon: {
    width: '80px',
    height: '80px',
    color: '#ff5a0f',
  },
  mediaControlBigIconDisabled: {
    width: '60px',
    height: '60px',
    color: '#ff5b0f76',
  },
  stepSelect: {
    margin: '0px 4px',
    fontSize: '14px',
  },
}));

type Props = {
  timeStep: number;
  isPlaying?: boolean;
  onChangeVideoPlaying?: (newPlayingState: boolean, logReason: string) => void;
  relativeSeek?: (timeDiff: number) => void;
  playbackSpeed?: number;
  setPlaybackSpeed?: (value: number) => void;
  setTimeStep?: React.Dispatch<React.SetStateAction<number>>;
  disablePausePlay?: boolean;
};

const playbackSpeeds: number[] = [0.125, 0.25, 0.5, 1, 1.5, 2, 3, 4, 5, 6];

const VideoControls = React.memo((props: Props) => {
  const classes = useStyles();

  const {
    timeStep,
    isPlaying,
    onChangeVideoPlaying,
    relativeSeek,
    playbackSpeed,
    setPlaybackSpeed,
    setTimeStep,
    disablePausePlay,
  } = props;

  const smallStep: number = useMemo(() => timeStep * 0.1, [timeStep]);

  const changeTimeStep = useCallback(
    (event) => {
      setTimeStep && setTimeStep(parseFloat(event.target.value));
    },
    [setTimeStep]
  );

  const toggleVideoPlayState = useCallback(() => {
    onChangeVideoPlaying && onChangeVideoPlaying(!isPlaying, 'Video button (or spacebar)');
  }, [isPlaying, onChangeVideoPlaying]);

  const decreasePlaybackSpeed = useCallback(() => {
    const curIndex: number = playbackSpeeds.findIndex((s) => s === playbackSpeed);
    const newIndex: number = curIndex === -1 ? 2 : Math.max(0, curIndex - 1);
    setPlaybackSpeed && setPlaybackSpeed(playbackSpeeds[newIndex]);
  }, [setPlaybackSpeed, playbackSpeed]);

  const increasePlaybackSpeed = useCallback(() => {
    const curIndex: number = playbackSpeeds.findIndex((s) => s === playbackSpeed);
    const newIndex: number = curIndex === -1 ? 2 : Math.min(playbackSpeeds.length - 1, curIndex + 1);
    setPlaybackSpeed && setPlaybackSpeed(playbackSpeeds[newIndex]);
  }, [playbackSpeed, setPlaybackSpeed]);

  const forwardStep = useCallback(() => {
    relativeSeek && relativeSeek(timeStep);
  }, [relativeSeek, timeStep]);

  const backStep = useCallback(() => {
    relativeSeek && relativeSeek(-timeStep);
  }, [relativeSeek, timeStep]);

  const forwardSmallStep = useCallback(() => {
    relativeSeek && relativeSeek(smallStep);
  }, [relativeSeek, smallStep]);

  const backSmallStep = useCallback(() => {
    relativeSeek && relativeSeek(-smallStep);
  }, [relativeSeek, smallStep]);

  // prevents UI focused onclick event
  const fixSpacebarFocusTrigger = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      toggleVideoPlayState();
    },
    [toggleVideoPlayState]
  );

  const spacedeps = useMemo(() => {
    return [fixSpacebarFocusTrigger];
  }, [fixSpacebarFocusTrigger]);

  const zdeps = useMemo(() => {
    return [decreasePlaybackSpeed];
  }, [decreasePlaybackSpeed]);
  const xdeps = useMemo(() => {
    return [increasePlaybackSpeed];
  }, [increasePlaybackSpeed]);
  const cdeps = useMemo(() => {
    return [backStep];
  }, [backStep]);
  const vdeps = useMemo(() => {
    return [backSmallStep];
  }, [backSmallStep]);
  const bdeps = useMemo(() => {
    return [forwardSmallStep];
  }, [forwardSmallStep]);
  const ndeps = useMemo(() => {
    return [forwardStep];
  }, [forwardStep]);

  useHotkeys('space', fixSpacebarFocusTrigger, { keydown: true, keyup: false }, spacedeps);

  useHotkeys('z', decreasePlaybackSpeed, { keydown: false, keyup: true }, zdeps);
  useHotkeys('x', increasePlaybackSpeed, { keydown: false, keyup: true }, xdeps);

  useHotkeys('c', backStep, { keydown: false, keyup: true }, cdeps);
  useHotkeys('v', backSmallStep, { keydown: false, keyup: true }, vdeps);
  useHotkeys('b', forwardSmallStep, { keydown: false, keyup: true }, bdeps);
  useHotkeys('n', forwardStep, { keydown: false, keyup: true }, ndeps);

  return (
    <div className={classes.container}>
      <div className={classes.containerItem}>
        <Tooltip title={isPlaying ? '[ space ] Pause' : '[ space ] Play'} arrow>
          <span>
            <IconButton onClick={toggleVideoPlayState} size="small" disabled={disablePausePlay}>
              {isPlaying ? (
                <PauseCircleOutlineIcon
                  className={disablePausePlay ? classes.mediaControlBigIconDisabled : classes.mediaControlBigIcon}
                />
              ) : (
                <PlayCircleOutlineIcon
                  className={disablePausePlay ? classes.mediaControlBigIconDisabled : classes.mediaControlBigIcon}
                />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </div>
      <div className={classes.containerItem}>
        <div className={classes.containerItemChild}>
          <Tooltip title="[ Z ] Decrease Playback Speed" arrow>
            <IconButton onClick={decreasePlaybackSpeed} size="small">
              <FastRewindIcon className={classes.mediaControlIcon} />
            </IconButton>
          </Tooltip>
          <Typography className={classes.inlineText}>{playbackSpeed}x</Typography>
          <Tooltip title="[ X ] Increase Playback Speed" arrow>
            <IconButton onClick={increasePlaybackSpeed} size="small">
              <FastForwardIcon className={classes.mediaControlIcon} />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.containerItemChild}>
          <Tooltip title={'[ C ] Step ' + timeStep + ' seconds back'} arrow>
            <IconButton onClick={backStep} size="small">
              <SkipPreviousOutlinedIcon className={classes.mediaControlIcon} />
            </IconButton>
          </Tooltip>

          <Tooltip title={'[ V ] Step ' + smallStep + ' seconds back'} arrow>
            <IconButton onClick={backSmallStep} size="small">
              <SkipPreviousIcon className={classes.mediaControlSmallIcon} />
            </IconButton>
          </Tooltip>

          <Select value={timeStep} onChange={changeTimeStep} className={classes.stepSelect}>
            <MenuItem value={1}>1</MenuItem>
            <MenuItem value={2}>2</MenuItem>
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
          </Select>

          <Tooltip title={'[ B ] Step ' + smallStep + ' seconds ahead'} arrow>
            <IconButton onClick={forwardSmallStep} size="small">
              <SkipNextIcon className={classes.mediaControlSmallIcon} />
            </IconButton>
          </Tooltip>

          <Tooltip title={'[ N ] Step ' + timeStep + ' seconds ahead'} arrow>
            <IconButton onClick={forwardStep} size="small">
              <SkipNextOutlinedIcon className={classes.mediaControlIcon} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

export default VideoControls;
